/**
 * CMS string interpolation.
 *
 * Content authored in the Puck editor may contain `{{token}}` placeholders
 * (e.g. `{{name}}`, `{{address}}`). Tokens are stored verbatim in the saved
 * page data so a single page can be served to many dispensaries; the actual
 * substitution happens at render time via Puck's `metadata` channel, read
 * inside each component's `render` function as `puck.metadata.variables`.
 *
 * The substitution is deliberately logic-less (a plain regex replace) — it
 * never evaluates code, so author-supplied templates cannot execute anything.
 */

export type CmsVariables = Record<string, string>;

/**
 * Tokens currently exposed to content authors. Kept in sync with
 * `buildCmsVariables` by a unit test so the two cannot drift.
 */
export const CMS_VARIABLE_KEYS = ['name', 'address'] as const;
export type CmsVariableKey = typeof CMS_VARIABLE_KEYS[number];

/**
 * Matches `{{token}}` where the token is a flat identifier (letters, digits,
 * underscore). Dotted/namespaced tokens are intentionally NOT matched — we only
 * resolve flat keys today, so `{{store.name}}` stays literal rather than
 * silently disappearing.
 */
const TOKEN_PATTERN = /{{\s*(\w+)\s*}}/g;

/**
 * Replace `{{token}}` placeholders in `text` with values from `variables`.
 *
 * - A token whose key is an OWN property of `variables` is always substituted,
 *   even when its value is an empty string (a real "this dispensary has no
 *   address" fact renders as blank rather than leaking `{{address}}` to
 *   customers).
 * - A token whose key is NOT an own property (author typo, unsupported token,
 *   or an inherited property like `constructor`/`toString`/`__proto__`) is left
 *   as the literal `{{token}}` so the mistake is visible during review.
 *
 * The own-property guard is load-bearing for security: without it,
 * `variables[key]` would fall through to `Object.prototype` and render
 * built-ins (e.g. `{{constructor}}` -> "function Object() { [native code] }").
 * The check uses `Object.prototype.hasOwnProperty.call` (rather than
 * `Object.hasOwn`, which is unavailable on this app's browser target floor).
 */
export function interpolate(text: string | undefined, variables: CmsVariables | undefined): string {
  if (!text || !variables) return text ?? '';
  return text.replace(TOKEN_PATTERN, (match, key: string) =>
    Object.prototype.hasOwnProperty.call(variables, key) ? variables[key] : match
  );
}

/** Shape we read from the dispensary object for interpolation. */
type DispensaryLike = { name?: string | null; address?: string | null } | null | undefined;

/**
 * Build the variable map served to a specific dispensary at render time.
 *
 * `address` is the dispensary's pre-formatted single-line address string. If we
 * later expose granular tokens (city/state/zip), source them from `location.*`.
 */
export function buildCmsVariables(dispensary: DispensaryLike): CmsVariables {
  return {
    name: dispensary?.name ?? '',
    address: dispensary?.address ?? '',
  };
}

/** Read the CMS variables that renderers pass through Puck's `metadata` channel. */
export function getCmsVariables(puck?: { metadata?: Record<string, unknown> }): CmsVariables | undefined {
  return puck?.metadata?.variables as CmsVariables | undefined;
}

/**
 * Convenience wrapper for component `render` functions: interpolate `text`
 * using the variables carried on Puck's `metadata`. Centralizes the metadata
 * access so a future metadata-shape change is a one-line fix here.
 */
export function interpolateFromPuck(text: string | undefined, puck?: { metadata?: Record<string, unknown> }): string {
  return interpolate(text, getCmsVariables(puck));
}

/**
 * Sample values used for editor preview. CMS content is authored once for many
 * dispensaries, so the editor has no single "current" dispensary — we preview
 * with representative sample values instead.
 */
export const EXAMPLE_CMS_VARIABLES: CmsVariables = {
  name: 'Green Leaf Dispensary',
  address: '123 Main St, Denver, CO 80202',
};
